/**
 * Component   : AddDoctorDialog
 * Description : Modal to register a new doctor user (POST /Auth/register)
 */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
} from "@mui/material";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import {
  callAPI,
  EPhone,
  formatPhoneNumber,
  Method,
} from "../../../commonFunctions/CommonFunctions";
import WSPhone from "../../../Components/Phone/WSPhone";
import WSSelectField from "../../../Components/Select/WSSelect";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import WSTextField from "../../../Components/TextBox/WSTextBox";

// Hospital dropdown source: every hospital, one request (kept outside the component so it stays referentially stable)
const hospitalSource = {
  url: "hospital/HospitalProfile/search",
  page: 1,
  pageSize: 100000,
  filter: [],
  sort: [],
};

const transFormHospitals = (data: any[] = []) =>
  data.map((item: any) => ({ value: item?.id, label: item?.hospitalName }));

interface AddDoctorDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddDoctorDialog: React.FC<AddDoctorDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { showMessage } = useSnackbar();
  const { handleSubmit, control, reset } = useForm();
  const [loading, setLoading] = useState(false);
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      const request = {
        name: data?.name,
        phoneNumber: formatPhoneNumber(data?.phoneNumber, EPhone.RemoveHyphen),
        userType: "doctor",
        createdByHospitalId: data?.createdByHospitalId,
      };
      const response: any = await callAPI("/Auth/register", Method.POST, request);
      if (response) {
        showMessage(response?.message, response?.isSuccess ? "success" : "error");
        if (response?.isSuccess) {
          handleClose();
          onSuccess?.();
          setShowProfilePrompt(true);
        }
      }
    } catch (e: any) {
      showMessage(e?.error || "Error adding doctor", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle sx={{ color: "#098963", fontWeight: "bold" }}>
            Add Doctor
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={12} sm={6}>
                <WSTextField
                  control={control}
                  id="name"
                  name="name"
                  rules={{ required: true }}
                  label="Name *"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <WSPhone
                  label="Phone"
                  rules={{ required: true }}
                  control={control}
                  id="phoneNumber"
                  name="phoneNumber"
                />
              </Grid>
              <Grid item xs={12}>
                <WSSelectField
                  control={control}
                  id="createdByHospitalId"
                  name="createdByHospitalId"
                  label="Hospital *"
                  rules={{ required: true }}
                  source={hospitalSource}
                  transFormData={transFormHospitals}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ borderColor: "#098963", color: "#098963", borderRadius: 2, px: 4 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                backgroundColor: "#098963",
                borderRadius: 2,
                px: 4,
                "&:hover": { backgroundColor: "#066e4f" },
              }}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={showProfilePrompt} onClose={() => setShowProfilePrompt(false)}>
        <DialogTitle sx={{ color: "#098963", fontWeight: "bold" }}>
          Doctor added
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            The doctor has been registered. Please request the doctor to
            complete their profile.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setShowProfilePrompt(false)}
            sx={{
              backgroundColor: "#098963",
              borderRadius: 2,
              px: 4,
              "&:hover": { backgroundColor: "#066e4f" },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddDoctorDialog;
